<script setup lang="ts">
import { ref } from 'vue'
import { Head, Link } from '@inertiajs/vue3'
import DashboardLayout from '@/Layouts/DashboardLayout.vue'
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/Components/ui/card'
import { BookOpen, Calendar, Plus, PoundSterling, Users } from 'lucide-vue-next'
import WeeklySchedule from '@/Components/Schedule/WeeklySchedule.vue'
import CalendarSessionModal from '@/Components/Schedule/CalendarSessionModal.vue'

interface NextSession {
  date: string
  time: string | null
  subject: string
  type: string
}

interface Stats {
  total_earnings: number
  active_students: number
  total_content: number
  next_session: NextSession | null
}

interface UpcomingSession {
  id: number
  schedule_id?: number
  title: string
  counterpart_name?: string
  subject: string
  date: string
  time: string
  raw_time?: string
  type: 'one-on-one' | 'group'
  status?: string
  duration_minutes?: number
  total_enrollments?: number
  lesson_links?: {
    room_id: string
    teacher_link: string
    student_link: string
    generated_at: string
  } | null
}

defineProps<{
  stats: Stats
  upcomingSessions: UpcomingSession[]
}>()

const showSessionModal = ref(false)
const selectedSession = ref<UpcomingSession | null>(null)

function handleSessionClick(session: UpcomingSession) {
  selectedSession.value = session
  showSessionModal.value = true
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(amount)
}
</script>

<template>
  <Head title="Teacher Dashboard" />

  <DashboardLayout>
    <div class="w-full max-w-6xl space-y-6">
      <div class="space-y-0.5">
        <h2 class="text-2xl font-bold tracking-tight">Dashboard</h2>
        <p class="text-gray-500">Overview of your teaching activity.</p>
      </div>

      <!-- Stats Grid -->
      <div class="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle class="text-sm font-medium">Total Earnings</CardTitle>
            <PoundSterling class="h-4 w-4 text-gray-500" />
          </CardHeader>
          <CardContent>
            <div class="text-2xl font-bold">{{ formatCurrency(stats.total_earnings) }}</div>
            <p class="text-xs text-gray-500">Net earnings to date</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle class="text-sm font-medium">Active Students</CardTitle>
            <Users class="h-4 w-4 text-gray-500" />
          </CardHeader>
          <CardContent>
            <div class="text-2xl font-bold">{{ stats.active_students }}</div>
            <p class="text-xs text-gray-500">Currently subscribed</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle class="text-sm font-medium">Content Items</CardTitle>
            <BookOpen class="h-4 w-4 text-gray-500" />
          </CardHeader>
          <CardContent>
            <div class="text-2xl font-bold">{{ stats.total_content }}</div>
            <p class="text-xs text-gray-500">Videos & lessons</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle class="text-sm font-medium">Next Session</CardTitle>
            <Calendar class="h-4 w-4 text-gray-500" />
          </CardHeader>
          <CardContent>
            <template v-if="stats.next_session">
              <div class="text-lg font-bold">{{ stats.next_session.date }}</div>
              <p class="text-xs text-gray-500">
                {{ stats.next_session.time ? `${stats.next_session.time} · ` : '' }}{{ stats.next_session.subject }}
              </p>
            </template>
            <template v-else>
              <div class="text-2xl font-bold">--</div>
              <p class="text-xs text-gray-500">No upcoming sessions</p>
            </template>
          </CardContent>
        </Card>
      </div>

      <!-- Quick Actions -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- Create Content Card -->
        <div class="bg-gradient-to-br from-teal-500 to-teal-800 rounded-2xl p-6 text-white">
          <h2 class="text-xl font-bold mb-2">Create New Content</h2>
          <p class="text-blue-100 mb-4">Share your knowledge with your students by creating video lessons, articles, or practice materials.</p>
          <Link
            href="/teacher/content/create"
            class="inline-flex items-center px-4 py-2 bg-white text-blue-600 rounded-lg font-medium hover:bg-blue-50 transition-colors"
          >
            <Plus class="w-4 h-4 mr-2" />
            Create Content
          </Link>
        </div>

        <!-- Manage Students Card -->
        <Card>
          <CardContent class="p-6">
            <h2 class="text-xl font-bold text-gray-900 mb-2">Manage Your Students</h2>
            <p class="text-gray-500 mb-4">View your subscribed students, track their progress, and manage their learning journey.</p>
            <Link
              href="/teacher/students"
              class="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              <Users class="w-4 h-4 mr-2" />
              View Students
            </Link>
          </CardContent>
        </Card>
      </div>

      <!-- Schedule -->
      <WeeklySchedule
        :lessons="upcomingSessions"
        calendar-url="/teacher/schedules"
        lesson-base-url="/teacher/lessons"
        @lesson-click="handleSessionClick"
      />

      <!-- Recent Activity -->
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
          <CardDescription>Latest updates from your students</CardDescription>
        </CardHeader>
        <CardContent>
          <div class="text-center py-12 text-gray-400">
            <BookOpen class="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p class="font-medium text-gray-600">Coming soon</p>
            <p class="text-sm mt-1">Student activity tracking is on its way.</p>
          </div>
        </CardContent>
      </Card>
    </div>

    <CalendarSessionModal
      :show="showSessionModal"
      :session="selectedSession"
      :can-edit-note="true"
      @close="showSessionModal = false"
    />
  </DashboardLayout>
</template>
